'use client'

import { useEffect, useMemo, useRef, useState } from 'react'

import { useMultiMarketDataStream } from '@/service/ws/use-market-data-stream'
import {
  type Exchange,
  MarketDataType,
  type Ticker,
  type TickerList,
} from '@/types/generated/common/types'

const FLUSH_MS = 1000

/** Keep fields a partial tick leaves out, so a value never blanks between full updates. */
export function mergeTicker(previous: Ticker | undefined, next: Ticker): Ticker {
  if (!previous) return next
  const merged: Ticker = { ...previous }
  for (const [key, value] of Object.entries(next) as [keyof Ticker, Ticker[keyof Ticker]][]) {
    if (value !== undefined && value !== '')
      (merged as unknown as Record<string, unknown>)[key] = value
  }
  return merged
}

/**
 * Live tickers for a set of instruments, keyed by symbol and flushed once a second: Home only
 * glances at prices, funding and open interest, so it doesn't need to re-render on every tick.
 */
export function useLiveTickers(
  instruments: readonly { symbol: string; exchange: Exchange }[]
): Map<string, Ticker> {
  const key = instruments.map((i) => `${i.exchange}:${i.symbol}`).join('|')
  const payloads = useMemo(
    () =>
      instruments.map((instrument) => ({
        symbol: instrument.symbol,
        exchange: instrument.exchange,
        dataType: MarketDataType.MARKET_DATA_TYPE_TICKER,
      })),
    // The list is rebuilt every render by callers; its identity is the instruments in it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key]
  )

  const pendingRef = useRef(new Map<string, Ticker>())
  const [tickers, setTickers] = useState<Map<string, Ticker>>(() => new Map())

  useMultiMarketDataStream({
    connected: payloads.length > 0,
    payloads,
    onMessage: (data: TickerList | undefined) => {
      for (const ticker of data?.tickers ?? []) {
        pendingRef.current.set(
          ticker.symbol,
          mergeTicker(pendingRef.current.get(ticker.symbol), ticker)
        )
      }
    },
  })

  useEffect(() => {
    const id = setInterval(() => {
      if (pendingRef.current.size === 0) return
      const pending = pendingRef.current
      pendingRef.current = new Map()
      setTickers((previous) => {
        const next = new Map(previous)
        for (const [symbol, ticker] of pending) {
          next.set(symbol, mergeTicker(previous.get(symbol), ticker))
        }
        return next
      })
    }, FLUSH_MS)
    return () => clearInterval(id)
  }, [])

  return tickers
}
